import { useCallback, useEffect, useMemo, useState } from 'react';
import { CalendarPlus, Edit, Eye, Plus, Search, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { listCustomers } from '../api/customers';
import {
  createPet,
  createPetEvent,
  deletePet,
  deletePetEvent,
  listPetEvents,
  listPets,
  listSpecies,
  updatePet,
  updatePetEvent
} from '../api/pets';
import { getErrorMessage } from '../api/response';
import Button from '../components/Button';
import ConfirmDialog from '../components/ConfirmDialog';
import FormField, { inputClassName } from '../components/FormField';
import Modal from '../components/Modal';
import PageHeader from '../components/PageHeader';
import PetDetailModal from '../components/PetDetailModal';
import StatusBadge from '../components/StatusBadge';
import Table from '../components/Table';
import { LoadingState } from '../components/PageState';
import useDebouncedValue from '../hooks/useDebouncedValue';
import { date, dateTime, fromInputDateTime, toInputDateTime } from '../utils/format';
import { activeStatus, getStatusConfig } from '../utils/status';

const emptyForm = {
  customer_id: '',
  name: '',
  species_id: '',
  breed: '',
  sex: '',
  birth_date: '',
  weight: '',
  color: '',
  microchip: '',
  is_sterilized: '',
  notes: '',
  status: 'active'
};

const emptyEventForm = {
  pet_id: '',
  title: '',
  description: '',
  event_type: 'manual',
  due_at: '',
  status: 'pending'
};

const eventStatus = {
  pending: { label: 'Pendiente', color: 'amber' },
  scheduled: { label: 'Agendado', color: 'blue' },
  completed: { label: 'Completado', color: 'green' },
  expired: { label: 'Expirado', color: 'red' }
};

const Mascotas = () => {
  const [pets, setPets] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [species, setSpecies] = useState([]);
  const [petEvents, setPetEvents] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [confirm, setConfirm] = useState(null);
  const [viewPet, setViewPet] = useState(null);
  const [eventModalOpen, setEventModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventForm, setEventForm] = useState(emptyEventForm);
  const [eventConfirm, setEventConfirm] = useState(null);
  const limit = 25;
  const debouncedSearch = useDebouncedValue(search.trim());

  const customerById = useMemo(() => {
    return new Map(customers.map((customer) => [Number(customer.id), customer]));
  }, [customers]);

  const loadPets = useCallback(async (overrides = {}) => {
    try {
      setLoading(true);
      const [petData, customerData, speciesData, eventData] = await Promise.all([
        listPets({ search: overrides.search ?? debouncedSearch, page: overrides.page ?? page, limit }),
        listCustomers({ limit: 100 }),
        listSpecies().catch(() => []),
        listPetEvents({ limit: 100 }).catch(() => [])
      ]);
      setPets(petData);
      setCustomers(customerData);
      setSpecies(speciesData);
      setPetEvents(eventData);
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudieron cargar las mascotas'));
    } finally {
      setLoading(false);
    }
  }, [limit, page, debouncedSearch]);

  useEffect(() => {
    loadPets();
  }, [loadPets]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (pet) => {
    setEditing(pet);
    setForm({
      customer_id: pet.customer_id || '',
      name: pet.name || '',
      species_id: pet.species_id || '',
      breed: pet.breed || pet.breed_label || '',
      sex: pet.sex || '',
      birth_date: pet.birth_date ? String(pet.birth_date).slice(0, 10) : '',
      weight: pet.weight ?? '',
      color: pet.color || '',
      microchip: pet.microchip || '',
      is_sterilized:
        pet.is_sterilized === 1 ? 'yes' : pet.is_sterilized === 0 ? 'no' : '',
      notes: pet.notes || '',
      status: pet.status || 'active'
    });
    setModalOpen(true);
  };

  const openEvent = (event = null, pet = null) => {
    setEditingEvent(event);
    setEventForm(event ? {
      pet_id: event.pet_id || '',
      title: event.title || '',
      description: event.description || '',
      event_type: event.event_type || 'manual',
      due_at: toInputDateTime(event.due_at || event.event_date),
      status: event.status || 'pending'
    } : {
      ...emptyEventForm,
      pet_id: pet?.id || ''
    });
    setEventModalOpen(true);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      setSaving(true);
      const payload = {
        ...form,
        customer_id: Number(form.customer_id),
        species_id: Number(form.species_id),
        weight: form.weight === '' ? null : Number(form.weight),
        is_sterilized: form.is_sterilized === '' ? null : form.is_sterilized
      };

      if (editing) {
        await updatePet(editing.id, payload);
        toast.success('Mascota actualizada');
      } else {
        await createPet(payload);
        toast.success('Mascota creada');
      }

      setModalOpen(false);
      loadPets();
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo guardar la mascota'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm) return;

    try {
      setSaving(true);
      await deletePet(confirm.id);
      toast.success('Mascota marcada como inactiva');
      setConfirm(null);
      loadPets();
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo inactivar la mascota'));
    } finally {
      setSaving(false);
    }
  };

  const handleEventSubmit = async (event) => {
    event.preventDefault();

    try {
      setSaving(true);
      const payload = {
        ...eventForm,
        pet_id: Number(eventForm.pet_id),
        due_at: fromInputDateTime(eventForm.due_at),
        description: eventForm.description || null
      };

      if (editingEvent) {
        await updatePetEvent(editingEvent.id, payload);
        toast.success('Evento actualizado');
      } else {
        await createPetEvent(payload);
        toast.success('Evento creado');
      }

      setEventModalOpen(false);
      loadPets();
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo guardar el evento'));
    } finally {
      setSaving(false);
    }
  };

  const handleEventDelete = async () => {
    if (!eventConfirm) return;

    try {
      setSaving(true);
      await deletePetEvent(eventConfirm.id);
      toast.success('Evento eliminado');
      setEventConfirm(null);
      loadPets();
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo eliminar el evento'));
    } finally {
      setSaving(false);
    }
  };

  const submitSearch = (event) => {
    event.preventDefault();
    setPage(1);
    loadPets({ page: 1, search: search.trim() });
  };

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setPage(1);
  };

  const columns = [
    {
      key: 'name',
      label: 'Mascota',
      render: (row) => (
        <div>
          <p className="font-semibold text-gray-900">{row.name}</p>
          <p className="text-xs text-gray-500">
            {row.species || '-'} · {row.breed || 'Sin raza'}
          </p>
        </div>
      )
    },
    {
      key: 'customer_id',
      label: 'Cliente',
      render: (row) => row.customer_name || customerById.get(Number(row.customer_id))?.full_name || 'Sin tutor'
    },
    {
      key: 'sex',
      label: 'Sexo',
      render: (row) => (row.sex === 'male' ? 'Macho' : row.sex === 'female' ? 'Hembra' : '-')
    },
    {
      key: 'sterilized',
      label: 'Esterilizada',
      render: (row) =>
        row.is_sterilized === 1 ? 'Sí' : row.is_sterilized === 0 ? 'No' : '-'
    },
    { key: 'birth_date', label: 'Nacimiento', render: (row) => date(row.birth_date) },
    { key: 'weight', label: 'Peso', render: (row) => (row.weight ? `${row.weight} kg` : '-') },
    {
      key: 'status',
      label: 'Estado',
      render: (row) => {
        const status = getStatusConfig(activeStatus, row.status);
        return <StatusBadge label={status.label} color={status.color} />;
      }
    },
    {
      key: 'actions',
      label: 'Acciones',
      render: (row) => (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setViewPet(row)}
            className="rounded-md p-2 text-gray-700 hover:bg-gray-100"
            title="Ver ficha"
          >
            <Eye className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => openEdit(row)}
            className="rounded-md p-2 text-blue-700 hover:bg-blue-50"
            title="Editar"
          >
            <Edit className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => openEvent(null, row)}
            className="rounded-md p-2 text-green-700 hover:bg-green-50"
            title="Agregar evento"
          >
            <CalendarPlus className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => setConfirm(row)}
            className="rounded-md p-2 text-red-700 hover:bg-red-50"
            title="Inactivar"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      )
    }
  ];

  const eventColumns = [
    {
      key: 'title',
      label: 'Evento',
      render: (row) => (
        <div>
          <p className="font-semibold text-gray-900">{row.title}</p>
          <p className="text-xs text-gray-500">{row.description || row.event_type || 'Manual'}</p>
        </div>
      )
    },
    { key: 'pet_name', label: 'Mascota', render: (row) => row.pet_name || `#${row.pet_id}` },
    { key: 'due_at', label: 'Fecha', render: (row) => dateTime(row.due_at || row.event_date) },
    {
      key: 'status',
      label: 'Estado',
      render: (row) => {
        const status = getStatusConfig(eventStatus, row.status);
        return <StatusBadge label={status.label} color={status.color} />;
      }
    },
    {
      key: 'actions',
      label: 'Acciones',
      render: (row) => (
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => openEvent(row)} className="rounded-md p-2 text-blue-700 hover:bg-blue-50" title="Editar">
            <Edit className="h-4 w-4" />
          </button>
          <button type="button" onClick={() => setEventConfirm(row)} className="rounded-md p-2 text-red-700 hover:bg-red-50" title="Eliminar">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Mascotas"
        description="Ficha completa con especie, raza, color, microchip, esterilización e historial clínico."
        actions={<Button icon={Plus} onClick={openCreate}>Nueva mascota</Button>}
      />

      <section className="rounded-lg border border-gray-200 bg-white p-4">
        <form onSubmit={submitSearch} className="flex flex-col gap-3 md:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              value={search}
              onChange={handleSearchChange}
              className={`${inputClassName} pl-9`}
              placeholder="Buscar mascota, tutor, especie, raza o microchip"
            />
          </div>
          <Button type="submit" variant="secondary">Buscar</Button>
        </form>
      </section>

      <section className="rounded-lg border border-gray-200 bg-white">
        {loading ? (
          <div className="p-5"><LoadingState rows={5} /></div>
        ) : (
          <Table columns={columns} data={pets} emptyMessage="No hay mascotas registradas" />
        )}
        <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3">
          <Button variant="secondary" disabled={page === 1 || loading} onClick={() => setPage((value) => value - 1)}>
            Anterior
          </Button>
          <span className="text-sm text-gray-600">Pagina {page}</span>
          <Button
            variant="secondary"
            disabled={pets.length < limit || loading}
            onClick={() => setPage((value) => value + 1)}
          >
            Siguiente
          </Button>
        </div>
      </section>

      <section className="rounded-lg border border-gray-200 bg-white">
        <div className="flex flex-col gap-3 border-b border-gray-200 p-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-950">Eventos próximos</h2>
            <p className="text-sm text-gray-500">Eventos manuales y automáticos que se muestran en el Home de la app.</p>
          </div>
          <Button icon={CalendarPlus} variant="secondary" onClick={() => openEvent()}>
            Nuevo evento
          </Button>
        </div>
        {loading ? (
          <div className="p-5"><LoadingState rows={4} /></div>
        ) : (
          <Table columns={eventColumns} data={petEvents} emptyMessage="No hay eventos próximos" />
        )}
      </section>

      <Modal
        open={modalOpen}
        title={editing ? 'Editar mascota' : 'Nueva mascota'}
        onClose={() => setModalOpen(false)}
        size="lg"
        footer={
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setModalOpen(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button type="submit" form="pet-form" disabled={saving}>
              {saving ? 'Guardando...' : 'Guardar'}
            </Button>
          </div>
        }
      >
        <form id="pet-form" onSubmit={handleSubmit} className="grid gap-4 md:grid-cols-2">
          <FormField label="Cliente" required>
            <select
              className={inputClassName}
              value={form.customer_id}
              onChange={(event) => setForm({ ...form, customer_id: event.target.value })}
              required
            >
              <option value="">Seleccionar cliente</option>
              {customers.map((customer) => (
                <option key={customer.id} value={customer.id}>{customer.full_name}</option>
              ))}
            </select>
          </FormField>
          <FormField label="Nombre" required>
            <input
              className={inputClassName}
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
              required
            />
          </FormField>
          <FormField label="Especie" required>
            <select
              className={inputClassName}
              value={form.species_id}
              onChange={(event) => setForm({ ...form, species_id: event.target.value })}
              required
            >
              <option value="">Seleccionar especie</option>
              {species.map((item) => (
                <option key={item.id} value={item.id}>{item.name}</option>
              ))}
            </select>
          </FormField>
          <FormField label="Raza">
            <input
              className={inputClassName}
              value={form.breed}
              onChange={(event) => setForm({ ...form, breed: event.target.value })}
              placeholder="Ej: Mestizo, Persa, Labrador..."
            />
          </FormField>
          <FormField label="Color">
            <input
              className={inputClassName}
              value={form.color}
              onChange={(event) => setForm({ ...form, color: event.target.value })}
              placeholder="Ej: Negro, Blanco y café"
            />
          </FormField>
          <FormField label="Microchip">
            <input
              className={inputClassName}
              value={form.microchip}
              onChange={(event) => setForm({ ...form, microchip: event.target.value })}
              placeholder="Código del chip"
            />
          </FormField>
          <FormField label="Castrada / operada">
            <select
              className={inputClassName}
              value={form.is_sterilized}
              onChange={(event) => setForm({ ...form, is_sterilized: event.target.value })}
            >
              <option value="">Sin informar</option>
              <option value="yes">Sí</option>
              <option value="no">No</option>
            </select>
          </FormField>
          <FormField label="Sexo">
            <select
              className={inputClassName}
              value={form.sex}
              onChange={(event) => setForm({ ...form, sex: event.target.value })}
            >
              <option value="">Sin informar</option>
              <option value="male">Macho</option>
              <option value="female">Hembra</option>
            </select>
          </FormField>
          <FormField label="Fecha nacimiento">
            <input
              type="date"
              className={inputClassName}
              value={form.birth_date}
              onChange={(event) => setForm({ ...form, birth_date: event.target.value })}
            />
          </FormField>
          <FormField label="Peso kg">
            <input
              type="number"
              step="0.01"
              className={inputClassName}
              value={form.weight}
              onChange={(event) => setForm({ ...form, weight: event.target.value })}
            />
          </FormField>
          <FormField label="Estado">
            <select
              className={inputClassName}
              value={form.status}
              onChange={(event) => setForm({ ...form, status: event.target.value })}
            >
              <option value="active">Activa</option>
              <option value="inactive">Inactiva</option>
              <option value="deceased">Fallecida</option>
            </select>
          </FormField>
          <div className="md:col-span-2">
            <FormField label="Observaciones">
              <textarea
                rows={3}
                className={inputClassName}
                value={form.notes}
                onChange={(event) => setForm({ ...form, notes: event.target.value })}
              />
            </FormField>
          </div>
        </form>
      </Modal>

      <Modal
        open={eventModalOpen}
        title={editingEvent ? 'Editar evento' : 'Nuevo evento'}
        onClose={() => setEventModalOpen(false)}
        size="lg"
        footer={
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setEventModalOpen(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button type="submit" form="pet-event-form" disabled={saving}>
              {saving ? 'Guardando...' : 'Guardar'}
            </Button>
          </div>
        }
      >
        <form id="pet-event-form" onSubmit={handleEventSubmit} className="grid gap-4 md:grid-cols-2">
          <FormField label="Mascota" required>
            <select
              className={inputClassName}
              value={eventForm.pet_id}
              onChange={(event) => setEventForm({ ...eventForm, pet_id: event.target.value })}
              required
            >
              <option value="">Seleccionar mascota</option>
              {pets.map((pet) => (
                <option key={pet.id} value={pet.id}>{pet.name}</option>
              ))}
            </select>
          </FormField>
          <FormField label="Titulo" required>
            <input
              className={inputClassName}
              value={eventForm.title}
              onChange={(event) => setEventForm({ ...eventForm, title: event.target.value })}
              placeholder="Ej: Control anual, vacuna, retiro de examen"
              required
            />
          </FormField>
          <FormField label="Fecha y hora" required>
            <input
              type="datetime-local"
              className={inputClassName}
              value={eventForm.due_at}
              onChange={(event) => setEventForm({ ...eventForm, due_at: event.target.value })}
              required
            />
          </FormField>
          <FormField label="Tipo">
            <select
              className={inputClassName}
              value={eventForm.event_type}
              onChange={(event) => setEventForm({ ...eventForm, event_type: event.target.value })}
            >
              <option value="manual">Manual</option>
              <option value="control">Control</option>
              <option value="vaccine">Vacuna</option>
              <option value="exam">Examen</option>
              <option value="medication">Medicamento</option>
              <option value="appointment">Cita</option>
              <option value="other">Otro</option>
            </select>
          </FormField>
          <FormField label="Estado">
            <select
              className={inputClassName}
              value={eventForm.status}
              onChange={(event) => setEventForm({ ...eventForm, status: event.target.value })}
            >
              <option value="pending">Pendiente</option>
              <option value="scheduled">Agendado</option>
              <option value="completed">Completado</option>
              <option value="expired">Expirado</option>
            </select>
          </FormField>
          <div className="md:col-span-2">
            <FormField label="Descripcion">
              <textarea
                rows={3}
                className={inputClassName}
                value={eventForm.description}
                onChange={(event) => setEventForm({ ...eventForm, description: event.target.value })}
              />
            </FormField>
          </div>
        </form>
      </Modal>

      <PetDetailModal
        pet={viewPet}
        open={Boolean(viewPet)}
        onClose={() => setViewPet(null)}
      />

      <ConfirmDialog
        open={Boolean(confirm)}
        title="Inactivar mascota"
        message={`La mascota ${confirm?.name} quedara inactiva.`}
        confirmLabel="Inactivar"
        loading={saving}
        onCancel={() => setConfirm(null)}
        onConfirm={handleDelete}
      />

      <ConfirmDialog
        open={Boolean(eventConfirm)}
        title="Eliminar evento"
        message={`El evento ${eventConfirm?.title} se eliminara de la agenda de la mascota.`}
        confirmLabel="Eliminar"
        loading={saving}
        onCancel={() => setEventConfirm(null)}
        onConfirm={handleEventDelete}
      />
    </div>
  );
};

export default Mascotas;
